import type { CanonicalEntity } from '@/shared/api/types';
import { moduleYawRadians } from './moduleOrientation';

export type ModuleFrame = {
  origin: [number, number, number];
  yawRad: number;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function vec3(value: unknown): [number, number, number] | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const out = value.slice(0, 3).map(Number);
  if (out.some((item) => !Number.isFinite(item))) return null;
  return [out[0], out[1], out[2]];
}

function num(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function entityCenterSize(entity: CanonicalEntity): { center: [number, number, number]; size: [number, number, number] } | null {
  const render = asRecord(entity.render);
  const center = vec3(render.center);
  const size = vec3(render.size);
  if (center && size) return { center, size };

  const aabb = asRecord(render.aabb);
  const mn = vec3(aabb.min);
  const mx = vec3(aabb.max);
  if (!mn || !mx) return null;
  return {
    center: [(mn[0] + mx[0]) / 2, (mn[1] + mx[1]) / 2, (mn[2] + mx[2]) / 2],
    size: [Math.max(0, mx[0] - mn[0]), Math.max(0, mx[1] - mn[1]), Math.max(0, mx[2] - mn[2])],
  };
}

/**
 * Object-local module frame used by facades, drawers, dimensions and animation.
 *
 * The backend should stamp `transform.originMm`, but generated/loaded bundles may
 * still only carry `rotationYDeg` + a world AABB. In that case derive the same
 * origin formula as `cabinet_domain.ModuleFrame.from_anchor`, so local module
 * coordinates do not collapse to world origin for rotated runs.
 */
export function deriveModuleFrame(module?: CanonicalEntity): ModuleFrame | null {
  if (!module) return null;
  const tx = asRecord((module as unknown as { transform?: unknown }).transform);
  const domain = asRecord(module.domain);
  const yawDeg = num(tx.rotationYDeg) ?? num(domain.rotationYDeg) ?? 0;
  const explicit = vec3(domain.originMm) ?? vec3(tx.originMm);
  if (explicit) return { origin: explicit, yawRad: moduleYawRadians(yawDeg) };

  const frame = entityCenterSize(module);
  if (!frame) return null;
  const yawBackend = (yawDeg * Math.PI) / 180;
  const cos = Math.cos(yawBackend);
  const sin = Math.sin(yawBackend);
  const hx = frame.size[0] / 2;
  const hy = frame.size[1] / 2;
  const hz = frame.size[2] / 2;
  return {
    origin: [
      frame.center[0] - hx * cos - hz * sin,
      frame.center[1] - hy,
      frame.center[2] + hx * sin - hz * cos,
    ],
    yawRad: moduleYawRadians(yawDeg),
  };
}

export function moduleLocalToWorldPoint(point: [number, number, number], frame: ModuleFrame | null): [number, number, number] {
  if (!frame) return [...point];
  const cos = Math.cos(frame.yawRad);
  const sin = Math.sin(frame.yawRad);
  return [
    frame.origin[0] + point[0] * cos - point[2] * sin,
    frame.origin[1] + point[1],
    frame.origin[2] + point[0] * sin + point[2] * cos,
  ];
}

export function moduleLocalDirectionToWorldVector(direction: [number, number, number], frame: ModuleFrame | null): [number, number, number] {
  const yaw = frame?.yawRad ?? 0;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  const x = direction[0] * cos - direction[2] * sin;
  const y = direction[1];
  const z = direction[0] * sin + direction[2] * cos;
  const len = Math.hypot(x, y, z) || 1;
  return [x / len, y / len, z / len];
}
